import React, { useState, useEffect } from 'react'
import { Loader2, CloudUpload, Edit, Trash2, ListChecks, RefreshCw, CheckSquare, Square } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Card, CardContent, CardFooter } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Progress } from '@/components/ui/progress'
import { Textarea } from '@/components/ui/textarea'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { toast } from 'sonner'
import { generateClient } from 'aws-amplify/data'
import type { Schema } from '../../amplify/data/resource'
import contentService, { ContentItem } from '../services/contentService'
import deviceService, { Device } from '../services/deviceService'
import clientService, { Client, Location } from '../services/clientService'
import contentAssignmentService from '../services/contentAssignmentService'

const client = generateClient<Schema>()

export default function ContentManager() {
  const [contentItems, setContentItems] = useState<ContentItem[]>([])
  const [devices, setDevices] = useState<Device[]>([])
  const [clients, setClients] = useState<Client[]>([])
  const [locations, setLocations] = useState<Location[]>([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [uploadTitle, setUploadTitle] = useState('')
  const [uploadDescription, setUploadDescription] = useState('')
  const [thumbnailUrls, setThumbnailUrls] = useState<Record<string, string>>({})
  
  const [selectedContentIds, setSelectedContentIds] = useState<string[]>([])
  
  const [assignDialogOpen, setAssignDialogOpen] = useState(false)
  const [assignmentTab, setAssignmentTab] = useState<'device' | 'group'>('device')
  const [selectedDevices, setSelectedDevices] = useState<string[]>([])
  const [selectedGroupType, setSelectedGroupType] = useState<'client' | 'location'>('client')
  const [selectedGroupId, setSelectedGroupId] = useState('')
  const [selectedClientForLocation, setSelectedClientForLocation] = useState('')
  const [assigning, setAssigning] = useState(false)
  
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [selectedContentForEdit, setSelectedContentForEdit] = useState<ContentItem | null>(null)
  const [editTitle, setEditTitle] = useState('')
  const [editDescription, setEditDescription] = useState('')
  const [editing, setEditing] = useState(false)

  const loadContent = async () => {
    try {
      setLoading(true)
      setError(null)
      const content = await contentService.getContent()
      setContentItems(content)
      
      const thumbnailPromises = content
        .filter(item => item.thumbnailS3Key)
        .map(async (item) => {
          try {
            const url = await contentService.getThumbnailUrl(item.thumbnailS3Key!)
            return { id: item.id, url }
          } catch (err) {
            console.warn(`Failed to load thumbnail for ${item.id}:`, err)
            return null
          }
        })
      
      const thumbnailResults = await Promise.all(thumbnailPromises)
      const thumbnailMap: Record<string, string> = {}
      
      thumbnailResults.forEach(result => {
        if (result) {
          thumbnailMap[result.id] = result.url
        }
      })
      
      setThumbnailUrls(thumbnailMap)
    } catch (err) {
      console.error('Error loading content:', err)
      setError('Failed to load content. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const loadDevices = async () => {
    try {
      const deviceList = await deviceService.getDevices()
      setDevices(deviceList)
    } catch (err) {
      console.error('Error loading devices:', err)
    }
  }

  const loadClients = async () => {
    try {
      const clientList = await clientService.listClients()
      setClients(clientList)
    } catch (err) {
      console.error('Error loading clients:', err)
    }
  }

  const loadLocations = async (clientId: string) => {
    try {
      const locationList = await clientService.listClientLocations(clientId)
      setLocations(locationList)
    } catch (err) {
      console.error('Error loading locations:', err)
      setLocations([])
    }
  }

  useEffect(() => {
    loadContent()
    loadDevices()
    loadClients()
    
    // Subscribe to Content updates for real-time changes
    const createSub = client.models.Content.onCreate({
      filter: {
        userId: { eq: '' } // Will be filtered by auth automatically
      }
    }).subscribe({
      next: async (data) => {
        if (data) {
          const newItem: ContentItem = {
            id: data.id,
            userId: data.userId,
            title: data.title,
            description: data.description || undefined,
            fileName: data.fileName,
            fileSize: data.fileSize || undefined,
            mimeType: data.mimeType || undefined,
            duration: data.duration || undefined,
            s3Key: data.s3Key,
            thumbnailS3Key: data.thumbnailS3Key || undefined,
            status: data.status || 'ready',
            tags: data.tags?.filter((tag): tag is string => tag !== null) || undefined,
            createdAt: data.createdAt || undefined,
            updatedAt: data.updatedAt || undefined,
          }
          setContentItems(prev => [newItem, ...prev])
        }
      },
      error: (error) => console.warn('onCreate subscription error:', error)
    })

    const updateSub = client.models.Content.onUpdate({
      filter: {
        userId: { eq: '' } // Will be filtered by auth automatically
      }
    }).subscribe({
      next: async (data) => {
        if (data) {
          const updatedItem: ContentItem = {
            id: data.id,
            userId: data.userId,
            title: data.title,
            description: data.description || undefined,
            fileName: data.fileName,
            fileSize: data.fileSize || undefined,
            mimeType: data.mimeType || undefined,
            duration: data.duration || undefined,
            s3Key: data.s3Key,
            thumbnailS3Key: data.thumbnailS3Key || undefined,
            status: data.status || 'ready',
            tags: data.tags?.filter((tag): tag is string => tag !== null) || undefined,
            createdAt: data.createdAt || undefined,
            updatedAt: data.updatedAt || undefined,
          }
          
          setContentItems(prev => 
            prev.map(item => item.id === updatedItem.id ? updatedItem : item)
          )
          
          // Load thumbnail if it was just added
          if (data.thumbnailS3Key && !thumbnailUrls[data.id]) {
            try {
              const url = await contentService.getThumbnailUrl(data.thumbnailS3Key)
              setThumbnailUrls(prev => ({ ...prev, [data.id]: url }))
            } catch (err) {
              console.warn(`Failed to load thumbnail for ${data.id}:`, err)
            }
          }
        }
      },
      error: (error) => console.warn('onUpdate subscription error:', error)
    })

    const deleteSub = client.models.Content.onDelete({
      filter: {
        userId: { eq: '' } // Will be filtered by auth automatically
      }
    }).subscribe({
      next: (data) => {
        if (data) {
          setContentItems(prev => prev.filter(item => item.id !== data.id))
          setThumbnailUrls(prev => {
            const { [data.id]: _, ...rest } = prev
            return rest
          })
        }
      },
      error: (error) => console.warn('onDelete subscription error:', error)
    })
    
    return () => {
      createSub.unsubscribe()
      updateSub.unsubscribe()
      deleteSub.unsubscribe()
    }
  }, [])

  const handleUploadClick = () => {
    setUploadDialogOpen(true)
  }

  const handleUploadClose = () => {
    setUploadDialogOpen(false)
    setSelectedFile(null)
    setUploadTitle('')
    setUploadDescription('')
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      setSelectedFile(file)
      if (!uploadTitle) {
        const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '')
        setUploadTitle(nameWithoutExt)
      }
    }
  }

  const handleUpload = async () => {
    if (!selectedFile || !uploadTitle.trim()) {
      return
    }

    try {
      setUploading(true)
      setUploadProgress(0)

      const newContent = await contentService.uploadContent({
        title: uploadTitle.trim(),
        description: uploadDescription.trim() || undefined,
        file: selectedFile,
        onProgress: (progress) => {
          const percentage = Math.round((progress.loaded / progress.total) * 100)
          setUploadProgress(percentage)
        },
      })

      setContentItems(prev => [newContent, ...prev])
      toast.success('Content uploaded successfully!')
      handleUploadClose()
    } catch (err) {
      console.error('Error uploading content:', err)
      setError('Failed to upload content. Please try again.')
    } finally {
      setUploading(false)
      setUploadProgress(0)
    }
  }

  const handleDeleteContent = async (contentId: string) => {
    if (!confirm('Are you sure you want to delete this content? This action cannot be undone.')) {
      return
    }

    try {
      await contentService.deleteContent(contentId)
      setContentItems(prev => prev.filter(item => item.id !== contentId))
      toast.success('Content deleted successfully!')
    } catch (err) {
      console.error('Error deleting content:', err)
      setError('Failed to delete content. Please try again.')
    }
  }

  const handleAssignClick = (content?: ContentItem) => {
    if (content) {
      setSelectedContentIds([content.id])
    }
    setSelectedDevices([])
    setSelectedGroupType('client')
    setSelectedGroupId('')
    setSelectedClientForLocation('')
    setAssignmentTab('device')
    setAssignDialogOpen(true)
  }

  const handleAssignClose = () => {
    setAssignDialogOpen(false)
    setSelectedDevices([])
    setSelectedGroupId('')
    setSelectedClientForLocation('')
  }

  const handleAssignContent = async () => {
    if (selectedContentIds.length === 0) {
      return
    }

    if (assignmentTab === 'device' && selectedDevices.length === 0) {
      return
    }

    if (assignmentTab === 'group' && !selectedGroupId) {
      return
    }

    try {
      setAssigning(true)
      
      for (const contentId of selectedContentIds) {
        if (assignmentTab === 'device') {
          await contentAssignmentService.assignContent({
            contentId,
            targetType: 'device',
            targetIds: selectedDevices,
          })
        } else {
          await contentAssignmentService.assignContent({
            contentId,
            targetType: 'group',
            targetIds: [selectedGroupId],
          })
        }
      }
      
      const targetCount = assignmentTab === 'device' ? selectedDevices.length : 1
      const targetType = assignmentTab === 'device' ? 'device(s)' : selectedGroupType
      toast.success(
        `${selectedContentIds.length} content item(s) assigned to ${targetCount} ${targetType} successfully!`
      )
      setSelectedContentIds([])
      handleAssignClose()
    } catch (err) {
      console.error('Error assigning content:', err)
      setError('Failed to assign content. Please try again.')
    } finally {
      setAssigning(false)
    }
  }

  const handleEditClick = (content: ContentItem) => {
    setSelectedContentForEdit(content)
    setEditTitle(content.title)
    setEditDescription(content.description || '')
    setEditDialogOpen(true)
  }

  const handleEditClose = () => {
    setEditDialogOpen(false)
    setSelectedContentForEdit(null)
    setEditTitle('')
    setEditDescription('')
  }

  const handleEditContent = async () => {
    if (!selectedContentForEdit || !editTitle.trim()) {
      return
    }

    try {
      setEditing(true)
      const updatedContent = await contentService.updateContent(selectedContentForEdit.id, {
        title: editTitle.trim(),
        description: editDescription.trim() || undefined,
      })
      
      setContentItems(prev => 
        prev.map(item => 
          item.id === updatedContent.id ? updatedContent : item
        )
      )
      toast.success('Content updated successfully!')
      handleEditClose()
    } catch (err) {
      console.error('Error updating content:', err)
      setError('Failed to update content. Please try again.')
    } finally {
      setEditing(false)
    }
  }

  const handleContentSelect = (contentId: string) => {
    setSelectedContentIds(prev => 
      prev.includes(contentId)
        ? prev.filter(id => id !== contentId)
        : [...prev, contentId]
    )
  }

  const handleSelectAll = () => {
    if (selectedContentIds.length === contentItems.length) {
      setSelectedContentIds([])
    } else {
      setSelectedContentIds(contentItems.map(item => item.id))
    }
  }

  const handleBulkAssign = () => {
    if (selectedContentIds.length === 0) {
      setError('Please select at least one content item to assign')
      return
    }
    handleAssignClick()
  }

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '0 Bytes'
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    if (bytes === 0) return '0 Bytes'
    const i = Math.floor(Math.log(bytes) / Math.log(1024))
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i]
  }

  const formatDuration = (seconds?: number) => {
    if (!seconds) return 'Unknown'
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const secs = seconds % 60
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Unknown'
    return new Date(dateString).toLocaleString()
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-4xl font-bold">Content Management</h1>
        <div className="flex gap-2">
          {selectedContentIds.length > 0 && (
            <Button
              variant="secondary"
              onClick={handleBulkAssign}
            >
              <ListChecks className="mr-2 h-4 w-4" />
              Assign Selected ({selectedContentIds.length})
            </Button>
          )}
          <Button
            variant="outline"
            onClick={loadContent}
            disabled={loading}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
          <Button onClick={handleUploadClick}>
            <CloudUpload className="mr-2 h-4 w-4" />
            Upload Content
          </Button>
        </div>
      </div>

      {selectedContentIds.length > 0 && (
        <Alert>
          <AlertDescription className="flex items-center justify-between">
            <span>{selectedContentIds.length} content item(s) selected</span>
            <Button size="sm" variant="ghost" onClick={() => setSelectedContentIds([])}>
              Clear Selection
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      
      {contentItems.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <CloudUpload className="h-16 w-16 text-muted-foreground mb-4" />
            <h3 className="text-xl font-semibold mb-2">No content uploaded yet</h3>
            <p className="text-sm text-muted-foreground mb-6 text-center">
              Upload your first video to get started with content distribution.
            </p>
            <Button onClick={handleUploadClick}>
              <CloudUpload className="mr-2 h-4 w-4" />
              Upload Your First Video
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="flex items-center gap-2 mb-4">
            <Button
              variant="outline"
              size="sm"
              onClick={handleSelectAll}
            >
              {selectedContentIds.length === contentItems.length ? (
                <>
                  <CheckSquare className="mr-2 h-4 w-4" />
                  Deselect All
                </>
              ) : (
                <>
                  <Square className="mr-2 h-4 w-4" />
                  Select All
                </>
              )}
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {contentItems.map((item) => (
              <Card key={item.id} className="relative overflow-hidden">
                <div className="absolute top-2 left-2 z-10">
                  <Checkbox
                    checked={selectedContentIds.includes(item.id)}
                    onCheckedChange={() => handleContentSelect(item.id)}
                    className="bg-white/90 border-2"
                  />
                </div>
                <div className="aspect-video bg-muted flex items-center justify-center overflow-hidden">
                  {item.thumbnailS3Key && thumbnailUrls[item.id] ? (
                    <img
                      src={thumbnailUrls[item.id]}
                      alt={item.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none'
                      }}
                    />
                  ) : (
                    <p className="text-sm text-muted-foreground">No thumbnail</p>
                  )}
                </div>
                <CardContent className="pt-4">
                  <h3 className="font-semibold truncate mb-2">{item.title}</h3>
                  {item.description && (
                    <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                      {item.description}
                    </p>
                  )}
                  <div className="space-y-1 text-xs text-muted-foreground">
                    <p>Duration: {formatDuration(item.duration)}</p>
                    <p>Size: {formatFileSize(item.fileSize)}</p>
                    <p>Status: {item.status || 'ready'}</p>
                    <p>Uploaded: {formatDate(item.createdAt)}</p>
                  </div>
                </CardContent>
                <CardFooter className="gap-2 pt-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleAssignClick(item)}
                  >
                    <ListChecks className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEditClick(item)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteContent(item.id)}
                  >
                    <Trash2 stroke="red" className="h-4 w-4" />
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        </>
      )}

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Upload Content</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <input
                accept="video/*"
                style={{ display: 'none' }}
                id="file-upload"
                type="file"
                onChange={handleFileSelect}
              />
              <label htmlFor="file-upload">
                <Button
                  variant="outline"
                  className="w-full"
                  asChild
                >
                  <span>
                    <CloudUpload className="mr-2 h-4 w-4" />
                    {selectedFile ? selectedFile.name : 'Choose Video File'}
                  </span>
                </Button>
              </label>
            </div>
            
            {selectedFile && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="title">Title *</Label>
                  <Input
                    id="title"
                    autoFocus
                    value={uploadTitle}
                    onChange={(e) => setUploadTitle(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="uploadDescription">Description (Optional)</Label>
                  <Textarea
                    id="uploadDescription"
                    rows={3}
                    value={uploadDescription}
                    onChange={(e) => setUploadDescription(e.target.value)}
                  />
                </div>
              </>
            )}

            {uploading && (
              <div className="space-y-2">
                <p className="text-sm">Uploading... {uploadProgress}%</p>
                <Progress value={uploadProgress} />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleUploadClose} disabled={uploading}>
              Cancel
            </Button>
            <Button 
              onClick={handleUpload} 
              disabled={!selectedFile || !uploadTitle.trim() || uploading}
            >
              {uploading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Uploading...
                </>
              ) : (
                'Upload'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assignment Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>
              Assign Content ({selectedContentIds.length} item{selectedContentIds.length !== 1 ? 's' : ''})
            </DialogTitle>
          </DialogHeader>
          <Tabs value={assignmentTab} onValueChange={(v) => setAssignmentTab(v as 'device' | 'group')}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="device">Assign to Devices</TabsTrigger>
              <TabsTrigger value="group">Assign to Group</TabsTrigger>
            </TabsList>
            
            <TabsContent value="device" className="space-y-4">
              <div className="space-y-2">
                <Label>Select Devices</Label>
                <div className="border rounded-lg p-4 max-h-[300px] overflow-y-auto space-y-2">
                  {devices.length === 0 ? (
                    <Alert>
                      <AlertDescription>
                        No devices available. Register devices first to assign content.
                      </AlertDescription>
                    </Alert>
                  ) : (
                    devices.map((device) => (
                      <div key={device.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={device.id}
                          checked={selectedDevices.includes(device.id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedDevices(prev => [...prev, device.id])
                            } else {
                              setSelectedDevices(prev => prev.filter(id => id !== device.id))
                            }
                          }}
                        />
                        <label
                          htmlFor={device.id}
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex-1 cursor-pointer"
                        >
                          {device.deviceName}
                          <span className="text-muted-foreground ml-2">
                            {device.status} • {device.location || 'No location'}
                          </span>
                        </label>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="group" className="space-y-4">
              <RadioGroup value={selectedGroupType} onValueChange={(v) => {
                setSelectedGroupType(v as 'client' | 'location')
                setSelectedGroupId('')
                if (v === 'location' && clients.length > 0) {
                  setSelectedClientForLocation(clients[0].id)
                  loadLocations(clients[0].id)
                }
              }}>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="client" id="client" />
                  <Label htmlFor="client">Client (All devices in client)</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="location" id="location" />
                  <Label htmlFor="location">Location (Devices at location)</Label>
                </div>
              </RadioGroup>

              {selectedGroupType === 'client' ? (
                <div className="space-y-2">
                  <Label>Select Client</Label>
                  <Select value={selectedGroupId} onValueChange={setSelectedGroupId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a client" />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.length === 0 ? (
                        <SelectItem value="none" disabled>No clients available</SelectItem>
                      ) : (
                        clients.map((client) => (
                          <SelectItem key={client.id} value={client.id}>
                            {client.name}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Select Client First</Label>
                    <Select value={selectedClientForLocation} onValueChange={(v) => {
                      setSelectedClientForLocation(v)
                      loadLocations(v)
                      setSelectedGroupId('')
                    }}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a client" />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.map((client) => (
                          <SelectItem key={client.id} value={client.id}>
                            {client.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {locations.length > 0 && (
                    <div className="space-y-2">
                      <Label>Select Location</Label>
                      <Select value={selectedGroupId} onValueChange={setSelectedGroupId}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a location" />
                        </SelectTrigger>
                        <SelectContent>
                          {locations.map((location) => (
                            <SelectItem key={location.id} value={location.id}>
                              {location.name} {location.city && `- ${location.city}`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              )}

              <Alert>
                <AlertDescription>
                  Content will be automatically assigned to all devices in the selected {selectedGroupType}.
                </AlertDescription>
              </Alert>
            </TabsContent>
          </Tabs>
          <DialogFooter>
            <Button variant="outline" onClick={handleAssignClose} disabled={assigning}>
              Cancel
            </Button>
            <Button 
              onClick={handleAssignContent} 
              disabled={
                assigning || 
                (assignmentTab === 'device' && selectedDevices.length === 0) ||
                (assignmentTab === 'group' && !selectedGroupId)
              }
            >
              {assigning ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Assigning...
                </>
              ) : (
                'Assign Content'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit Content</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="editTitle">Title *</Label>
              <Input
                id="editTitle"
                autoFocus
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="editDescription">Description (Optional)</Label>
              <Textarea
                id="editDescription"
                rows={3}
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleEditClose} disabled={editing}>
              Cancel
            </Button>
            <Button 
              onClick={handleEditContent} 
              disabled={!editTitle.trim() || editing}
            >
              {editing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
